import { visibilityManager } from './visibilityManager';

export const DEFAULT_VERSION_CHECK_THROTTLE_MS = 60_000;

export function buildIdsDiffer(bundledBuildId: string, remoteBuildId: string | undefined): boolean {
  return Boolean(remoteBuildId && remoteBuildId !== bundledBuildId);
}

export function userMayBeEditingForm(): boolean {
  if (typeof document === 'undefined') {
    return false;
  }
  const el = document.activeElement;
  if (!el) {
    return false;
  }
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') {
    return true;
  }
  return (el as HTMLElement).isContentEditable;
}

export function shouldReloadForNewBuild(params: {
  pendingReload: boolean;
  isVisible: boolean;
  userEditing: boolean;
}): boolean {
  return params.pendingReload && params.isVisible && !params.userEditing;
}

export interface BuildVersionMonitorOptions {
  throttleMs?: number;
  fetchImpl?: typeof fetch;
  reloadImpl?: () => void;
  nowImpl?: () => number;
}

/**
 * Polls /version.json on load and when the tab becomes visible (throttled).
 * Reloads when the deployed build differs from the bundled build, deferring
 * while the user is mid-form or the tab is hidden.
 */
export function initBuildVersionMonitor(options: BuildVersionMonitorOptions = {}): void {
  if (typeof window === 'undefined' || import.meta.env.DEV) {
    return;
  }

  const bundledBuildId = import.meta.env.VITE_BUILD_ID;
  if (!bundledBuildId) {
    return;
  }

  const fetchImpl = options.fetchImpl ?? fetch.bind(window);
  const reloadImpl = options.reloadImpl ?? (() => window.location.reload());
  const nowImpl = options.nowImpl ?? Date.now;
  const throttleMs = options.throttleMs ?? DEFAULT_VERSION_CHECK_THROTTLE_MS;

  let pendingReload = false;
  let lastCheck = 0;

  const maybeReload = () => {
    if (
      shouldReloadForNewBuild({
        pendingReload,
        isVisible: document.visibilityState === 'visible',
        userEditing: userMayBeEditingForm(),
      })
    ) {
      reloadImpl();
    }
  };

  const checkVersion = async () => {
    const now = nowImpl();
    if (now - lastCheck < throttleMs) {
      return;
    }
    lastCheck = now;

    try {
      const response = await fetchImpl(`/version.json?_=${now}`, { cache: 'no-store' });
      if (!response.ok) {
        return;
      }
      const data = (await response.json()) as { buildId?: string };
      if (buildIdsDiffer(bundledBuildId, data.buildId)) {
        pendingReload = true;
        maybeReload();
      }
    } catch {
      // Ignore network errors; will retry on next visibility check.
    }
  };

  void checkVersion();

  visibilityManager.subscribe((visible) => {
    if (!visible) {
      return;
    }
    void checkVersion();
    maybeReload();
  });

  document.addEventListener('focusout', maybeReload, { passive: true });
}
